import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

/**
 * CohortSendDialog — PHASE 8.35
 *
 * Powers the post-publish "Send to incomplete" + "Send to waitlist" buttons.
 *
 * Default action: one-click "Send to all (N)". A small "send to specific user"
 * link expands a search-as-you-type single-user picker, used for QA — admin
 * picks one real user, fires the email to that account only, verifies they
 * see the right thing, then comes back and sends to the rest.
 *
 * Dedup is server-side via `wave_cohort_sends`. Already-sent rows render with
 * a "✓ SENT" badge and are NOT selectable.
 *
 * Each cohort uses its own template + routing URL (configured server-side):
 *   incomplete → /login → /panelist/profile?after=/panelist/survey/{wave_id}
 *   waitlist   → /set-password (signed token) → profile → survey
 */
export default function CohortSendDialog({ open, onClose, wave, cohort, onSent }) {
  const [counts, setCounts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("all");          // "all" | "single"
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [searching, setSearching] = useState(false);

  // Reset state whenever the dialog opens fresh for a new wave/cohort
  useEffect(() => {
    if (!open) return;
    setMode("all");
    setQuery("");
    setRows([]);
    setSelectedId("");
    setCounts(null);
    api.get(`/admin/waves/${wave.id}/cohort-summary`)
      .then((r) => setCounts(r.data?.[cohort] || { total: 0, already_sent: 0, not_yet_sent: 0 }))
      .catch(() => toast.error("Could not load cohort counts"));
  }, [open, wave?.id, cohort]);

  // Search picker — load rows when admin switches to "single" mode or types.
  useEffect(() => {
    if (!open || mode !== "single") return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/admin/waves/${wave.id}/recipients`, {
          params: { cohort, q: query, limit: 100 },
        });
        if (!cancelled) setRows(r.data?.rows || []);
      } catch (_e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, mode, query, wave?.id, cohort]);

  const send = async () => {
    if (mode === "single" && !selectedId) {
      toast.error("Pick one user from the list first.");
      return;
    }
    if (mode === "all" && !window.confirm(`Send the survey email to ${counts?.not_yet_sent || 0} ${cohort} user${(counts?.not_yet_sent || 0) === 1 ? "" : "s"}? This can't be undone.`)) {
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/admin/waves/${wave.id}/send-cohort`, {
        cohort,
        // Translate the FE-internal "single" mode label to the backend's
        // canonical "selected" value. Bug fix: backend rejected "single".
        mode: mode === "single" ? "selected" : "all",
        user_ids: mode === "single" ? [selectedId] : [],
      });
      const sent = r.data?.sent || 0;
      toast.success(`Sent ${sent} email${sent === 1 ? "" : "s"} to ${cohort} cohort.`);
      onSent && onSent();
      onClose && onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const cohortLabel = cohort === "incomplete" ? "Incomplete profile" : "Waitlist (passwordless)";
  const cohortDesc = cohort === "incomplete"
    ? "Has set a password (real claim, not a ghost) but hasn't finished their DNA profile yet. Same definition as the 'Incomplete profile' tab on /admin/users. Email link routes to login → /panelist/profile → /panelist/survey."
    : "No working password on file — covers brand-new direct signups AND legacy_waitlist stubs whose seeded password hash was never claimed (no password_set_at + no last_login_at). Same definition as the 'Password pending' tab on /admin/users. Email link is a signed set-password URL (name + email locked) → profile → survey.";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-xl rounded-none border-2 border-black" data-testid={`cohort-send-dialog-${cohort}`}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-black tracking-tight">Send to {cohortLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="border-l-4 border-mango pl-3">
            <div className="font-bold">{wave?.name}</div>
            <div className="text-xs text-neutral-600 mt-0.5">{cohortDesc}</div>
          </div>

          {!counts ? (
            <div className="text-neutral-500 text-xs">Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-neutral-100 p-3 border-2 border-neutral-300">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-600">Eligible</div>
                  <div className="text-2xl font-black mt-1" data-testid={`cohort-total-${cohort}`}>{counts.total}</div>
                </div>
                <div className="bg-emerald-50 p-3 border-2 border-emerald-300">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">Not yet emailed</div>
                  <div className="text-2xl font-black mt-1 text-emerald-900" data-testid={`cohort-not-yet-${cohort}`}>{counts.not_yet_sent}</div>
                </div>
                <div className="bg-neutral-100 p-3 border-2 border-neutral-300 opacity-70">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-600">Already sent</div>
                  <div className="text-2xl font-black mt-1 text-neutral-700" data-testid={`cohort-sent-${cohort}`}>{counts.already_sent}</div>
                </div>
              </div>

              {/* Default mode = send to all */}
              {mode === "all" && (
                <div className="space-y-3">
                  <div className="text-xs text-neutral-600">
                    Clicking <strong>Send to all</strong> below will email <strong>{counts.not_yet_sent} {cohort} user{counts.not_yet_sent === 1 ? "" : "s"}</strong> who haven&apos;t received this wave&apos;s invite yet. The {counts.already_sent} already-sent recipient{counts.already_sent === 1 ? "" : "s"} {counts.already_sent === 1 ? "is" : "are"} skipped automatically.
                  </div>
                  <button
                    type="button"
                    onClick={() => setMode("single")}
                    className="text-[12px] text-mango underline hover:text-mango/80"
                    data-testid={`cohort-pick-user-${cohort}`}
                  >
                    or send to a specific user (for QA testing) →
                  </button>
                </div>
              )}

              {/* Single-user picker — search + click to select */}
              {mode === "single" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { setMode("all"); setSelectedId(""); }}
                      className="text-xs underline text-neutral-600"
                      data-testid={`cohort-back-to-all-${cohort}`}
                    >
                      ← back to send-to-all
                    </button>
                  </div>
                  <Input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by name or email…"
                    className="rounded-none"
                    data-testid={`cohort-search-${cohort}`}
                    autoFocus
                  />
                  <div className="border-2 border-neutral-200 max-h-72 overflow-y-auto">
                    {searching && <div className="p-3 text-center text-xs text-neutral-500">Searching…</div>}
                    {!searching && rows.length === 0 && (
                      <div className="p-3 text-center text-xs text-neutral-500 italic">No users match.</div>
                    )}
                    {!searching && rows.map((u) => {
                      const isSelected = selectedId === u.id;
                      const blocked = u.already_sent;
                      return (
                        <button
                          type="button"
                          key={u.id}
                          disabled={blocked}
                          onClick={() => !blocked && setSelectedId(u.id)}
                          data-testid={`cohort-row-${u.id}`}
                          className={`w-full text-left p-2 border-b border-neutral-100 text-xs flex items-center gap-2 ${
                            blocked ? "bg-neutral-50 text-neutral-400 cursor-not-allowed"
                            : isSelected ? "bg-mango/10 border-mango"
                            : "hover:bg-neutral-50"
                          }`}
                        >
                          <input
                            type="radio"
                            name={`pick-${cohort}`}
                            checked={isSelected}
                            disabled={blocked}
                            readOnly
                          />
                          <div className="flex-1 min-w-0">
                            <div className={`font-bold truncate ${blocked ? "line-through" : ""}`}>{u.email}</div>
                            <div className="text-[11px] text-neutral-500 truncate">
                              {u.name || "—"} {u.city ? `· ${u.city}` : ""} {u.region ? `· ${u.region}` : ""} {u.is_overseas ? "· Overseas" : ""}
                            </div>
                          </div>
                          {blocked && <span className="text-[10px] font-mono bg-neutral-300 text-neutral-700 px-1.5 py-0.5">✓ SENT</span>}
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] text-neutral-500">
                    Showing the first 100 matches. Refine your search if you need to find a specific user.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none" data-testid={`cohort-cancel-${cohort}`}>Cancel</Button>
          <Button
            onClick={send}
            disabled={busy || !counts || (mode === "single" && !selectedId) || (mode === "all" && (counts?.not_yet_sent || 0) === 0)}
            className="bg-black hover:bg-neutral-800 text-white rounded-none"
            data-testid={`cohort-send-${cohort}`}
          >
            {busy ? "Sending…"
              : mode === "single" ? "Send to selected user"
              : `Send to all ${counts?.not_yet_sent || 0}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
